import React, { useEffect, useState } from 'react';
import {
  SafeAreaView,
  ScrollView,
  StyleSheet,
  View,
  TouchableOpacity,
  Linking,
  Dimensions,
  Platform,
  useWindowDimensions,
  Alert,
} from 'react-native';
import Modal from 'react-native-modal';
import { Icon, Text } from 'react-native-elements';
import lodash from 'lodash';

const debug = require('debug')('se:advertoverlay');

import { getTopImageUrl } from '../../lib/advert';
import { trackAdvertAction, trackAdvertDetailView } from '../../lib/analytics';
import Carousel, { DisplayType } from '../zonecarousel';
import Divider from '../divider';
import ExploreImage from '../exploreimage';
import phoneLink from './phoneLink';

import {
  Advertisement,
  Image as AdvertImage,
  Action,
  ActionType,
  Tag,
} from '../../../api/adserver';
import config from '../../lib/config';
import { openLocation, withHttp } from '../../lib/linking';

import AdvertContactList from './AdvertContactList';

const maxWidth = 600;

export interface Props {
  item?: Advertisement | null;
  onClose: () => void;
  onPressTag: (tag: Tag) => void;
}

interface AdvertAction {
  id: string;
  title: string;
  icon: string | null;
  action: Action;
}

const AdvertOverlay: React.FC<Props> = (props) => {
  const [visible, setVisible] = useState(true);
  const { item } = props;
  const { width: windowWidth } = useWindowDimensions();

  useEffect(() => {
    if (item) {
      trackAdvertDetailView(item);
    }
  }, [item]);

  const showClose = true;

  if (!item) {
    return null;
  }

  const onClose = () => {
    setVisible(false);
    const { onClose: handleOnClose } = props;
    handleOnClose();
  };

  let topImageUrl = getTopImageUrl(item);

  let advertActions: AdvertAction[] = [];
  if (item.actions) {
    advertActions = item.actions.map<AdvertAction>((action) => {
      const icon = lodash.get(action, 'icon.image.url', null);
      return {
        id: action.id,
        title: action.title,
        icon,
        action,
      };
    });
  }

  const handleOnPressTag = (tag: Tag) => {
    debug(`tag: ${tag.id} pressed`);
    const { onPressTag } = props;
    if (onPressTag) {
      onPressTag(tag);
    }
  };

  const onPressAction = (actionItem: AdvertAction) => {
    debug('onPressAction');
    const { action } = actionItem;
    trackAdvertAction(
      action.title,
      item.headline,
      item.categories[0].title,
      item.id.toString(),
      item.categories[0].id.toString(),
    );
    switch (action.action_type) {
      case ActionType.Email: {
        const { url: emailAddress } = action;
        if (!emailAddress) {
          debug('email not set.  not linking');
          break;
        }
        Linking.openURL(`mailto:${emailAddress}`)
          .then(() => debug('opened email'))
          .catch((e) => debug(`error opening email.  ${e.toString()}`));
        break;
      }
      case ActionType.Web: {
        const { url: webUrl } = action;
        if (!webUrl) {
          debug('url not set.  not linking');
          break;
        }
        const linkUrl = withHttp(webUrl);
        Linking.openURL(linkUrl)
          .then(() => debug(`opened url:${linkUrl}`))
          .catch((e) => debug(`error opening url.  ${e.toString()}`));
        break;
      }
      case ActionType.Phone: {
        const { url: phoneNumber } = action;
        if (!phoneNumber) {
          debug('phoneNumber not set.  not linking');
          break;
        }
        const url = `tel:${phoneNumber}`;
        phoneLink(url);
        break;
      }
      case ActionType.Directions: {
        const { latitude, longitude } = item.closest_location;
        if (!latitude || !longitude) {
          debug('lat/lng not set.  not opening directions');
          break;
        }
        openLocation(latitude, longitude, item);
        break;
      }
      default: {
        debug(
          `unknown onPressContact itemType: ${
            action.action_type
          }, action: ${JSON.stringify(action)}, advert.id: ${
            item.id
          }.  Doing nothing`,
        );
      }
    }
  };

  const hasGallery = item.carousel_images && item.carousel_images.length > 0;

  let advertCaroselImages: AdvertImage[] = [];
  if (hasGallery && item.carousel_images) {
    advertCaroselImages = item.carousel_images;
  }

  const calculatedWidth = Math.min(maxWidth, windowWidth);
  console.log(`calculatedWidth: ${calculatedWidth}`);

  const modalDimStyles = {
    ...Platform.select({
      web: {
        maxWidth: calculatedWidth,
        margin: 'auto',
        // marginBottom: 40,
      },
    }),
  };

  const modalStyle = StyleSheet.flatten([styles.overlay, modalDimStyles]);

  const mainImageDimStyles = {
    ...Platform.select({
      web: {
        width: calculatedWidth,
        height: calculatedWidth * config.theme.advert.imageHeightRatio,
      },
      default: {
        height: windowWidth * config.theme.advert.imageHeightRatio,
        width: windowWidth,
      },
    }),
  };

  const mainImageStyle = StyleSheet.flatten([
    styles.mainImage,
    mainImageDimStyles,
  ]);

  return (
    <Modal
      backdropColor={'#000000'}
      backdropOpacity={0.5}
      isVisible={visible}
      hideModalContentWhileAnimating
      style={modalStyle}
      onBackdropPress={onClose}
    >
      {showClose && (
        <View style={styles.topOverlay}>
          <View style={styles.closeButtonContainer}>
            <TouchableOpacity onPress={onClose}>
              <Icon
                tvParallaxProperties={null}
                activeOpacity={0.5}
                name={'close'}
                size={30}
                color={'#505050'}
                style={styles.closeButtonImage}
              />
            </TouchableOpacity>
          </View>
        </View>
      )}
      <SafeAreaView style={styles.scrollViewContainer}>
        <ScrollView
          contentInsetAdjustmentBehavior="automatic"
          style={styles.scrollView}
        >
          {topImageUrl && (
            <>
              <ExploreImage uri={topImageUrl} style={mainImageStyle} />
              <Divider />
            </>
          )}
          <View style={[styles.featureHeadingContainer, styles.sectionStart]}>
            <Text style={styles.featureHeading}>{item.headline}</Text>
            <Text style={[styles.detailText, styles.bottomMargin1Em]}>
              {item.bi_line}
            </Text>
            <Carousel
              displayType={DisplayType.badges}
              items={item.tags}
              onPressBadge={handleOnPressTag}
              name="adverttags"
            />
          </View>
          <Divider />
          <Carousel
            name="advertactions"
            displayType={DisplayType.mini}
            items={advertActions}
            onPressData={onPressAction}
            singleLine
          />
          <Divider />
          <View style={[styles.featureHeadingContainer, styles.sectionStart]}>
            <Text style={[styles.featureHeading, styles.subSectionHeading]}>
              More Info
            </Text>
            <Text style={[styles.detailText, styles.subSectionBody]}>
              {item.body}
            </Text>
          </View>
          <Divider />
          <View style={[styles.featureHeadingContainer, styles.sectionStart]}>
            <Text style={[styles.featureHeading, styles.subSectionHeading]}>
              Contact
            </Text>
            <AdvertContactList advert={item} />
          </View>
          {hasGallery && (
            <>
              <Divider />
              <View
                style={[
                  styles.featureHeadingContainer,
                  styles.sectionStart,
                  styles.screenEnd,
                ]}
              >
                <Text style={[styles.featureHeading, styles.subSectionHeading]}>
                  Gallery
                </Text>
                <Carousel
                  name="advertcarousel"
                  displayType={DisplayType.photo}
                  items={advertCaroselImages}
                />
              </View>
            </>
          )}
        </ScrollView>
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    // backgroundColor: 'white',
    // borderRadius: 10,
    // width: '100%',
    // marginHorizontal: 0,
    // padding: 0,
    margin: 0,
    marginTop: 40,
  },
  closeButtonContainer: {
    height: '100%',
    // width: 30,
    // backgroundColor: 'orange',
    zIndex: 1000,
    flex: 1,
    // marginStart: 10,
    alignContent: 'center',
    alignItems: 'flex-start',
    justifyContent: 'center',
    marginStart: 10,
  },
  closeButtonImage: {
    //    width: 30,
    //    marginStart: 10,
  },
  topOverlay: {
    flex: 1,
    position: 'absolute',
    opacity: 0.5,
    borderTopStartRadius: 10,
    borderTopEndRadius: 10,
    overflow: 'hidden',
    right: 0,
    top: 0,
    width: '100%',
    height: 40,
    fontWeight: 'bold',
    zIndex: 1001,
    backgroundColor: '#ffffff',
    padding: 0,
    marginTop: 0,
  },
  scrollView: {
    backgroundColor: '#ffffff',
    marginHorizontal: 0,
    borderRadius: 10,
  },
  featureHeadingContainer: {
    marginBottom: 5,
    marginHorizontal: 10,
  },
  sectionStart: {
    marginTop: 20,
    marginBottom: 20,
  },
  featureHeading: {
    fontSize: config.theme.headingFontSize,
    fontWeight: config.theme.headingFontWeight,
    color: config.theme.headingTextColor,
    marginBottom: 10,
  },
  subSectionHeading: {
    fontSize: 18,
    color: config.theme.advert.subSectionHeadingTextColor,
    fontWeight: '700',
  },
  subSectionBody: {
    color: config.theme.advert.subSectionHeadingTextColor,
  },
  mainImage: {},
  detailText: {
    fontSize: 14,
  },
  bottomMargin1Em: {
    marginBottom: 14,
  },
  screenEnd: {
    marginBottom: 20,
  },
  scrollViewContainer: {
    ...Platform.select({
      web: {
        flex: 1,
      },
    }),
  },
});

export default AdvertOverlay;
